import React, { useState, useEffect } from "react";
import useFontFamilies from "../../hooks/useFontFamilies";
import { useDropzone } from "react-dropzone";
import {
    Box,
    Tabs,
    Tab,
    Typography,
    Divider,
    Alert,
    Snackbar,
} from "@mui/material";
import {
    Image,
    ColorLens,
    TextFields,
    Map,
    Chat,
    Settings,
    Palette,
} from "@mui/icons-material";
import { MuiColorInput } from "mui-color-input";
import ToggleSwitch from "../backend/ToggleSwitch";
import { usePxToVw } from "../../hooks/usePxToVw";
import useS3Upload from "../../hooks/useS3Upload";
import UseToast from "../UseToast";

// MediaDropzone Component (same as before)
const MediaDropzone = ({ onDrop, selectedFile }) => {
    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: {
            'image/*': ['.jpeg', '.jpg', '.png', '.gif'],
            'video/*': ['.mp4', '.mov', '.mp3'],
            'application/pdf': ['.pdf'],
        },
        multiple: false,
    });

    return (
        <div
            {...getRootProps()}
            style={{
                border: '2px dashed #cccccc',
                padding: '20px',
                borderRadius: '5px',
                textAlign: 'center',
                cursor: 'pointer',
                minHeight: '100px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <input {...getInputProps()} />
            {selectedFile ? (
                <p>{selectedFile.name}</p>
            ) : (
                <p>Drag and drop a file here, or click to select a file</p>
            )}
        </div>
    );
};

// Helper function to check file type (same as before)
const getFileType = (fileUrl) => {
    if (!fileUrl) return 'blank';
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.svg'];
    const videoExtensions = ['.mp4', '.mov', '.avi', '.mkv', '.webm'];
    if (imageExtensions.some(ext => fileUrl.toLowerCase().endsWith(ext))) {
        return 'image';
    }
    if (videoExtensions.some(ext => fileUrl.toLowerCase().endsWith(ext))) {
        return 'video';
    }
    return 'other';
};

// UploadButton Component (same as before)
const UploadButton = ({ uploading, onUpload, buttonColor, ...props }) => (
    <button
        onClick={onUpload}
        variant="contained"
        disabled={uploading}
        style={{
            marginTop: '20px',
            backgroundColor: buttonColor,
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            ...props.style,
        }}
    >
        {uploading ? 'Uploading...' : 'Upload'}
    </button>
);

// UploadedUrl Component (same as before)
const UploadedUrl = ({ url }) => {
    if (!url || !url.fileUrl) return null;
    return (
        <div
            style={{
                marginTop: '10px',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                gap: '10px',
            }}
        >
            <div style={{ float: 'left', width: '70%', marginTop: '15px' }}>
                Uploaded File:{' '}
                <a
                    href={url.fileUrl}
                    style={{ fontWeight: 'bold' }}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {url.fileName || 'Download File'}
                </a>
            </div>
        </div>
    );
};

// UploadedUrlImage Component (same as before)
const UploadedUrlImage = ({ url }) => {
    if (!url || !url.fileUrl) return null;
    return (
        <>
            {url ? (
                <div style={{ margin: 'auto' }}>
                    {getFileType(url.fileUrl) === 'image' ? (
                        <a href={url.fileUrl} target="_blank" rel="noreferrer">
                            <img src={url.fileUrl} style={{ maxWidth: '210px', maxHeight: '80px' }} alt="" />
                        </a>
                    ) : getFileType(url.fileUrl) === 'video' ? (
                        <a href={url.fileUrl} target="_blank" rel="noreferrer">
                            <img
                                src="https://paulavatar.s3.us-east-2.amazonaws.com/play-button.png"
                                style={{ maxWidth: '200px', maxHeight: '60px' }}
                                alt="Play Video"
                            />
                        </a>
                    ) : (
                        ''
                    )}
                </div>
            ) : null}
        </>
    );
};

// TabPanel Component for Tabs
const TabPanel = ({ children, value, index, ...other }) => {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`settings-tabpanel-${index}`}
            aria-labelledby={`settings-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
};

const MobileScreenSettings = ({
    userId,
    buttonColor,
    value
}) => {
    const fontOptions = useFontFamilies('option');
    const [settings, setSettings] = useState({
        fontFamily: "Arial, sans-serif",
        headerLogo: {
            enabled: false,
            fileUrl: "",
            fileName: "",
            width: 100,
        },
        headerPadding: 10,
        headerMarginBottom: 10,
        header: {
            title: "",
            titleColor: "#000000",
            titleFontSize: "18",
            locationTextColor: "#000000",
            locationTextFontSize: "14",
            marginBottom: 10,
            subtitle: "",
            backgroundColor: "#ffffff",
            fontWeight: '600',
        },
        buttons: {
            gap: "10",
            mapButton: {
                text: "Map",
                borderWidth: "1",
                buttonWidth: "100",
                buttonHeight: "40",
                buttonPadding: "10",
                buttonFontSize: "14",
                buttonTextColor: "#ffffff",
                buttonActiveTextColor: "#ffffff",
                borderColor: "#000000",
                borderRadius: "0",
                backgroundColor: "#1976d2",
                activeBackgroundColor: "#115293",
            },
            textButton: {
                text: "Text",
                borderWidth: "1",
                buttonWidth: "100",
                buttonHeight: "40",
                buttonPadding: "10",
                buttonFontSize: "14",
                buttonTextColor: "#ffffff",
                buttonActiveTextColor: "#ffffff",
                borderColor: "#000000",
                borderRadius: "0",
                backgroundColor: "#1976d2",
                activeBackgroundColor: "#115293",
            },
        },
        avatarText: "",
        textSettings: {
            fontSize: "16",
            fontFamily: "Arial, sans-serif",
        },
        mapPagePadding: 20,
        textPagePadding: 20,
    });

    const [activeTab, setActiveTab] = useState(0);
    const [fetchLoading, setFetchLoading] = useState(true);
    const [saveLoading, setSaveLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success"
    });

    // State for file uploads
    const [logoFile, setLogoFile] = useState(null);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [uploadedLogoUrl, setUploadedLogoUrl] = useState(
        settings?.headerLogo?.fileUrl
            ? { fileUrl: settings.headerLogo.fileUrl, fileName: settings.headerLogo.fileName }
            : null
    );

    const { showSuccessToast, showErrorToast } = UseToast();
    const { uploadFile, uploading: s3Uploading, error: s3Error } = useS3Upload({
        onSuccess: (fileUrl, file) => {
            const fileName = file?.name || fileUrl.split('/').pop();
            setUploadedLogoUrl({ fileUrl, fileName });
            handleChange('headerLogo.fileUrl', fileUrl);
            handleChange('headerLogo.fileName', fileName);
        },
        onError: (err) => {
            console.error('S3 upload error', err);
            showSnackbar('Failed to upload logo', 'error');
        },
    });

    const { pxToVw } = usePxToVw();

    // Fetch settings on component mount
    useEffect(() => {
        fetchSettings();
    }, [userId, value]);

    const fetchSettings = async () => {
        if (!userId) return;
        try {
            setFetchLoading(true);
            const response = await fetch(`/api/wayfinder/mobile-screen-settings/${userId}`);
            const data = await response.json();
            if (data.success) {
                const mergedSettings = {
                    ...data.data,
                    headerLogo: {
                        ...settings.headerLogo,
                        ...(data.data.headerLogo || {}),
                        enabled: data.data.headerLogo?.enabled ?? false,
                        fileUrl: data.data.headerLogo?.fileUrl || "",
                        fileName: data.data.headerLogo?.fileName || "",
                        width: data.data.headerLogo?.width ?? 100,
                    },
                    headerPadding: data.data.headerPadding ?? 10,
                    headerMarginBottom: data.data.headerMarginBottom ?? 10,
                    header: {
                        ...settings.header,
                        ...(data.data.header || {}),
                        titleFontSize: data.data.header?.titleFontSize ?? "18",
                        locationTextFontSize: data.data.header?.locationTextFontSize ?? "14",
                        marginBottom: data.data.header?.marginBottom ?? 10,
                        fontWeight: data.data.header?.fontWeight ?? '600',
                    },
                    buttons: {
                        gap: data.data.buttons?.gap ?? "10",
                        mapButton: {
                            ...settings.buttons.mapButton,
                            ...(data.data.buttons?.mapButton || {}),
                            borderWidth: data.data.buttons?.mapButton?.borderWidth ?? "1",
                            buttonWidth: data.data.buttons?.mapButton?.buttonWidth ?? "100",
                            buttonHeight: data.data.buttons?.mapButton?.buttonHeight ?? "40",
                            buttonPadding: data.data.buttons?.mapButton?.buttonPadding ?? "10",
                            buttonFontSize: data.data.buttons?.mapButton?.buttonFontSize ?? "14",
                            buttonTextColor: data.data.buttons?.mapButton?.buttonTextColor ?? "#ffffff",
                            buttonActiveTextColor: data.data.buttons?.mapButton?.buttonActiveTextColor ?? "#ffffff",
                            borderColor: data.data.buttons?.mapButton?.borderColor ?? "#000000",
                            borderRadius: data.data.buttons?.mapButton?.borderRadius ?? "0",
                        },
                        textButton: {
                            ...settings.buttons.textButton,
                            ...(data.data.buttons?.textButton || {}),
                            borderWidth: data.data.buttons?.textButton?.borderWidth ?? "1",
                            buttonWidth: data.data.buttons?.textButton?.buttonWidth ?? "100",
                            buttonHeight: data.data.buttons?.textButton?.buttonHeight ?? "40",
                            buttonPadding: data.data.buttons?.textButton?.buttonPadding ?? "10",
                            buttonFontSize: data.data.buttons?.textButton?.buttonFontSize ?? "14",
                            buttonTextColor: data.data.buttons?.textButton?.buttonTextColor ?? "#ffffff",
                            buttonActiveTextColor: data.data.buttons?.textButton?.buttonActiveTextColor ?? "#ffffff",
                            borderColor: data.data.buttons?.textButton?.borderColor ?? "#000000",
                            borderRadius: data.data.buttons?.textButton?.borderRadius ?? "0",
                        },
                    },
                    textSettings: {
                        fontSize: data.data.textSettings?.fontSize ?? "16",
                        fontFamily: data.data.textSettings?.fontFamily ?? "Arial, sans-serif",
                    },
                    mapPagePadding: data.data.mapPagePadding ?? 20,
                    textPagePadding: data.data.textPagePadding ?? 20,
                };
                setSettings(mergedSettings);
                if (data.data.headerLogo?.fileUrl) {
                    setUploadedLogoUrl({
                        fileUrl: data.data.headerLogo.fileUrl,
                        fileName: data.data.headerLogo.fileName || 'Uploaded Logo'
                    });
                }
            } else {
                console.error('Failed to fetch settings:', data.message);
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
            showSnackbar('Error fetching settings', 'error');
        } finally {
            setFetchLoading(false);
        }
    };

    const handleChange = (path, value) => {
        const keys = path.split('.');
        const newSettings = { ...settings };
        keys.reduce((obj, currentKey, index) => {
            if (index === keys.length - 1) {
                obj[currentKey] = value;
            } else {
                obj[currentKey] = { ...obj[currentKey] };
            }
            return obj[currentKey];
        }, newSettings);
        setSettings(newSettings);
    };

    const handleLogoDrop = (acceptedFiles) => {
        if (acceptedFiles && acceptedFiles.length > 0) {
            const file = acceptedFiles[0];
            setLogoFile(file);
            const previewUrl = URL.createObjectURL(file);
            handleChange("headerLogo.fileUrl", previewUrl);
            handleChange("headerLogo.fileName", file.name);
        }
    };

    const handleLogoUpload = async () => {
        if (!logoFile) {
            showErrorToast('Please select a file first', 'warning');
            return;
        }
        setUploadingLogo(true);
        try {
            const fileUrl = await uploadFile(logoFile);
            setSettings(prevSettings => ({
                ...prevSettings,
                headerLogo: {
                    ...prevSettings.headerLogo,
                    enabled: true,
                    fileUrl: fileUrl,
                    fileName: logoFile.name,
                }
            }));
            showSuccessToast('Logo uploaded successfully');
            return fileUrl;
        } catch (err) {
            console.error('Error uploading logo:', err);
            showErrorToast('Failed to upload logo', 'error');
            throw err;
        } finally {
            setUploadingLogo(false);
        }
    };

    const handleSubmit = async () => {
        if (!userId) {
            showSnackbar('User ID is required', 'error');
            return;
        }
        setSaveLoading(true);
        try {
            const saveData = {
                headerLogo: {
                    enabled: settings?.headerLogo?.enabled || false,
                    fileUrl: settings?.headerLogo?.fileUrl || "",
                    fileName: settings?.headerLogo?.fileName || "",
                    width: settings?.headerLogo?.width || 100,
                },
                headerPadding: settings?.headerPadding || 10,
                headerMarginBottom: settings?.headerMarginBottom || 10,
                header: {
                    title: settings?.header?.title || "",
                    titleColor: settings?.header?.titleColor || "#000000",
                    titleFontSize: settings?.header?.titleFontSize || "18",
                    locationTextColor: settings?.header?.locationTextColor || "#000000",
                    locationTextFontSize: settings?.header?.locationTextFontSize || "14",
                    marginBottom: settings?.header?.marginBottom || 10,
                    backgroundColor: settings?.header?.backgroundColor || "#ffffff",
                    fontWeight: settings?.header?.fontWeight || '600',
                },
                buttons: {
                    gap: settings?.buttons?.gap || "10",
                    mapButton: {
                        text: settings?.buttons?.mapButton?.text || "Map",
                        borderWidth: settings?.buttons?.mapButton?.borderWidth || "1",
                        buttonWidth: settings?.buttons?.mapButton?.buttonWidth || "100",
                        buttonHeight: settings?.buttons?.mapButton?.buttonHeight || "40",
                        buttonPadding: settings?.buttons?.mapButton?.buttonPadding || "10",
                        buttonFontSize: settings?.buttons?.mapButton?.buttonFontSize || "14",
                        buttonTextColor: settings?.buttons?.mapButton?.buttonTextColor || "#ffffff",
                        buttonActiveTextColor: settings?.buttons?.mapButton?.buttonActiveTextColor || "#ffffff",
                        borderColor: settings?.buttons?.mapButton?.borderColor || "#000000",
                        borderRadius: settings?.buttons?.mapButton?.borderRadius || "0",
                        backgroundColor: settings?.buttons?.mapButton?.backgroundColor || "#1976d2",
                        activeBackgroundColor: settings?.buttons?.mapButton?.activeBackgroundColor || "#115293",
                    },
                    textButton: {
                        text: settings?.buttons?.textButton?.text || "Text",
                        borderWidth: settings?.buttons?.textButton?.borderWidth || "1",
                        buttonWidth: settings?.buttons?.textButton?.buttonWidth || "100",
                        buttonHeight: settings?.buttons?.textButton?.buttonHeight || "40",
                        buttonPadding: settings?.buttons?.textButton?.buttonPadding || "10",
                        buttonFontSize: settings?.buttons?.textButton?.buttonFontSize || "14",
                        buttonTextColor: settings?.buttons?.textButton?.buttonTextColor || "#ffffff",
                        buttonActiveTextColor: settings?.buttons?.textButton?.buttonActiveTextColor || "#ffffff",
                        borderColor: settings?.buttons?.textButton?.borderColor || "#000000",
                        borderRadius: settings?.buttons?.textButton?.borderRadius || "0",
                        backgroundColor: settings?.buttons?.textButton?.backgroundColor || "#1976d2",
                        activeBackgroundColor: settings?.buttons?.textButton?.activeBackgroundColor || "#115293",
                    },
                },
                avatarText: settings?.avatarText || "",
                fontFamily: settings?.fontFamily || "Arial, sans-serif",
                textSettings: {
                    fontSize: settings?.textSettings?.fontSize || "16",
                    fontFamily: settings?.textSettings?.fontFamily || "Arial, sans-serif",
                },
                mapPagePadding: settings?.mapPagePadding || 20,
                textPagePadding: settings?.textPagePadding || 20,
            };

            const response = await fetch(`/api/wayfinder/save-mobile-screen-settings/${userId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(saveData),
            });

            const data = await response.json();
            if (data.success) {
                showSuccessToast('Settings saved successfully');
            } else {
                showErrorToast('Failed to save settings');
            }
        } catch (error) {
            console.error('Error saving settings:', error);
            showSnackbar('Error saving settings: ' + error.message, 'error');
        } finally {
            setSaveLoading(false);
        }
    };

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const renderPreview = (title, content) => (
        <Box mb={3}>
            <Typography variant="subtitle1" gutterBottom display="flex" alignItems="center" gap={1}>
                <ColorLens fontSize="small" />
                {title}
            </Typography>
            {content}
        </Box>
    );

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    if (fetchLoading) {
        return (
            <div className="pd-ltr-20 xs-pd-20-10">
                <div className="min-height-200px">
                    <div className="page-header">
                        <Typography variant="h4">Loading Mobile Screen Settings...</Typography>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            <div className="">
                <div className="min-height-200px">
                    {/* Tabs Navigation */}
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                        <Tabs
                            value={activeTab}
                            onChange={handleTabChange}
                            variant="scrollable"
                            scrollButtons="auto"
                            aria-label="mobile screen settings tabs"
                        >
                            <Tab
                                icon={<Image />}
                                iconPosition="start"
                                label="Logo"
                                sx={{ minHeight: '48px' }}
                            />
                            <Tab
                                icon={<TextFields />}
                                iconPosition="start"
                                label="Header"
                                sx={{ minHeight: '48px' }}
                            />
                            <Tab
                                icon={<TextFields />}
                                iconPosition="start"
                                label="Body"
                                sx={{ minHeight: '48px' }}
                            />
                            <Tab
                                icon={<Map />}
                                iconPosition="start"
                                label="Map Button"
                                sx={{ minHeight: '48px' }}
                            />
                            <Tab
                                icon={<Chat />}
                                iconPosition="start"
                                label="Text Button"
                                sx={{ minHeight: '48px' }}
                            />
                            <Tab
                                icon={<Palette />}
                                iconPosition="start"
                                label="Preview"
                                sx={{ minHeight: '48px' }}
                            />
                        </Tabs>
                    </Box>

                    {/* Logo Tab */}
                    <TabPanel value={activeTab} index={0}>
                        <div className="form-group menu-card2" style={{ height: 'auto', padding: '20px' }}>
                            <div className="form-group" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                                <label style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Header Logo</label>
                                <ToggleSwitch
                                    label="Enable"
                                    isOn={settings.headerLogo?.enabled}
                                    onChange={val => handleChange("headerLogo.enabled", val)}
                                    id="headerLogo"
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                                <MediaDropzone
                                    onDrop={handleLogoDrop}
                                    selectedFile={logoFile}
                                />
                                <UploadedUrlImage url={uploadedLogoUrl || settings.headerLogo} />
                            </div>

                            <UploadedUrl url={uploadedLogoUrl || settings.headerLogo} />

                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: '20px',
                                alignItems: 'center',
                                marginTop: '30px',
                            }}>
                                <UploadButton
                                    uploading={uploadingLogo}
                                    onUpload={handleLogoUpload}
                                    buttonColor={buttonColor}
                                    style={{ margin: '0', padding: '10px 30px' }}
                                />

                                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                    <label style={{ fontWeight: '600', whiteSpace: 'nowrap' }}>
                                        Size (%):
                                    </label>
                                    <input
                                        style={{ width: '100px' }}
                                        type="number"
                                        className="form-control"
                                        value={settings.headerLogo?.width || 100}
                                        onChange={(e) => handleChange('headerLogo.width', Number(e.target.value))}
                                        min={10}
                                    />
                                </div>
                            </div>
                        </div>
                    </TabPanel>

                    {/* Header Tab */}
                    <TabPanel value={activeTab} index={1}>
                        <div className="form-group">
                            {/* Header Preview */}
                            {renderPreview(
                                "Header Preview",
                                <Box
                                    sx={{
                                        backgroundColor: settings.header.backgroundColor,
                                        padding: pxToVw(settings.headerPadding || '4'),
                                        marginBottom: pxToVw(settings.headerMarginBottom || '10'),
                                        textAlign: 'center',
                                        borderRadius: '8px',
                                        border: '1px solid #ddd',
                                    }}
                                >
                                    <Typography variant="h5" fontWeight="bold" style={{
                                        fontFamily: settings.fontFamily || 'Arial',
                                        marginBottom: pxToVw(settings.header.marginBottom),
                                        fontWeight: settings.header.fontWeight || '600',
                                        color: settings.header.titleColor || '#000000',
                                        fontSize: pxToVw(settings.header.titleFontSize || '18'),
                                    }}>
                                        {settings.header.title || "Company Name"}
                                    </Typography>
                                    <Typography variant="subtitle1" style={{
                                        color: settings.header.locationTextColor || '#000000',
                                        fontSize: pxToVw(settings.header.locationTextFontSize || '14')
                                    }}>
                                        Location Name
                                    </Typography>
                                </Box>
                            )}

                            <div className="row" style={{ marginBottom: '20px' }}>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Company Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={settings.header.title || ""}
                                            onChange={(e) => handleChange("header.title", e.target.value)}
                                            placeholder="Enter company name"
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Title Font Size (px)</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={settings.header.titleFontSize || "18"}
                                            onChange={(e) => handleChange('header.titleFontSize', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="row" style={{ marginBottom: '20px' }}>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Location Text Font Size (px)</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={settings.header.locationTextFontSize || "14"}
                                            onChange={(e) => handleChange('header.locationTextFontSize', e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Font Weight</label>
                                        <select
                                            className="form-control"
                                            value={settings.header.fontWeight}
                                            onChange={(e) =>
                                                handleChange("header.fontWeight", e.target.value)}
                                        >
                                            <option value={100}>100 (Thin)</option>
                                            <option value={200}>200 (Extra Light)</option>
                                            <option value={300}>300 (Light)</option>
                                            <option value={400}>400 (Normal)</option>
                                            <option value={500}>500 (Medium)</option>
                                            <option value={600}>600 (Semi Bold)</option>
                                            <option value={700}>700 (Bold)</option>
                                            <option value={800}>800 (Extra Bold)</option>
                                            <option value={900}>900 (Black)</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="row" style={{ marginBottom: '20px' }}>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Font Family</label>
                                        <select
                                            id="font"
                                            value={settings.fontFamily || 'Arial'}
                                            onChange={e => handleChange('fontFamily', e.target.value)}
                                            className="form-select form-control"
                                        >
                                            {fontOptions}
                                        </select>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Title Margin Bottom (px)</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={settings.header.marginBottom ?? 10}
                                            onChange={(e) => handleChange('header.marginBottom', Number(e.target.value))}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="row" style={{ marginBottom: '20px' }}>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Header Background Color</label>
                                        <MuiColorInput
                                            format="hex"
                                            value={settings.header.backgroundColor || "#ffffff"}
                                            onChange={(value) => handleChange("header.backgroundColor", value)}
                                            fullWidth
                                            size="small"
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Title Color</label>
                                        <MuiColorInput
                                            format="hex"
                                            value={settings.header.titleColor || '#000000'}
                                            onChange={(value) => handleChange('header.titleColor', value)}
                                            fullWidth
                                            size="small"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="row" style={{ marginBottom: '20px' }}>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Location Text Color</label>
                                        <MuiColorInput
                                            format="hex"
                                            value={settings.header.locationTextColor || '#000000'}
                                            onChange={(value) => handleChange('header.locationTextColor', value)}
                                            fullWidth
                                            size="small"
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Header Padding (px)</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={settings.headerPadding ?? 10}
                                            onChange={(e) => handleChange('headerPadding', Number(e.target.value))}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Header Margin Bottom (px)</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={settings.headerMarginBottom ?? 10}
                                            onChange={(e) => handleChange('headerMarginBottom', Number(e.target.value))}
                                        />
                                    </div>
                                </div>
                                {/* <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Avatar Text Message</label>
                                        <textarea
                                            className="form-control"
                                            rows="3"
                                            value={settings.avatarText || ""}
                                            onChange={(e) => handleChange('avatarText', e.target.value)}
                                            placeholder="Enter message to display in text directions"
                                        />
                                    </div>
                                </div> */}
                            </div>
                        </div>
                    </TabPanel>

                       <TabPanel value={activeTab} index={2}>
                        <div className="form-group">
                            <div className="row" style={{ marginBottom: '20px' }}>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Mobile Text Font Size (px)</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={settings.textSettings?.fontSize || "16"}
                                            onChange={(e) => handleChange('textSettings.fontSize', e.target.value)}
                                            placeholder="16"
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Mobile Text Font Family</label>
                                        <select
                                            className="form-control"
                                            value={settings.textSettings?.fontFamily || 'Arial, sans-serif'}
                                            onChange={(e) => handleChange('textSettings.fontFamily', e.target.value)}
                                        >
                                            {fontOptions}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="row" style={{ marginBottom: '20px' }}>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Map Page Padding (px)</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={settings.mapPagePadding || 20}
                                            onChange={(e) => handleChange('mapPagePadding', parseInt(e.target.value) || 20)}
                                            placeholder="20"
                                            min="0"
                                        />
                                        <small className="form-text text-muted">
                                            Left and right padding for the map page content
                                        </small>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Text Page Padding (px)</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={settings.textPagePadding || 20}
                                            onChange={(e) => handleChange('textPagePadding', parseInt(e.target.value) || 20)}
                                            placeholder="20"
                                            min="0"
                                        />
                                        <small className="form-text text-muted">
                                            Left and right padding for the text page content
                                        </small>
                                    </div>
                                </div>
                            </div>

                           
                           

                    
                    

                           
                           
                        </div>
                    </TabPanel>

                    {/* Map Button Tab */}
                    <TabPanel value={activeTab} index={3}>
                        <div className="form-group">
                            {/* Map Button Preview */}
                            {renderPreview(
                                "Map Button Preview",
                                <Box display="flex" gap={3} flexWrap="wrap">
                                    <button
                                        style={{
                                            height: pxToVw(settings.buttons.mapButton.buttonHeight) || '40px',
                                            backgroundColor: settings.buttons.mapButton.backgroundColor,
                                            borderRadius: pxToVw(settings.buttons.mapButton.borderRadius) || '0px',
                                            fontSize: pxToVw(settings.buttons.mapButton.buttonFontSize) || '14px',
                                            fontFamily: settings.fontFamily || 'Arial',
                                            color: settings.buttons.mapButton.buttonTextColor || '#ffffff',
                                            padding: pxToVw(settings.buttons.mapButton.buttonPadding) || '10px',
                                            width: pxToVw(settings.buttons.mapButton.buttonWidth || '5vw'),
                                            border: `${pxToVw(settings.buttons.mapButton.borderWidth)} solid ${settings.buttons.mapButton.borderColor}`,
                                            marginRight: '20px',
                                        }}
                                    >
                                        {settings.buttons.mapButton.text || "Map"}
                                    </button>
                                    <button
                                        style={{
                                            height: pxToVw(settings.buttons.mapButton.buttonHeight) || '40px',
                                            backgroundColor: settings.buttons.mapButton.activeBackgroundColor,
                                            borderRadius: pxToVw(settings.buttons.mapButton.borderRadius) || '0px',
                                            fontSize: pxToVw(settings.buttons.mapButton.buttonFontSize) || '14px',
                                            fontFamily: settings.fontFamily || 'Arial',
                                            color: settings.buttons.mapButton.buttonActiveTextColor || '#ffffff',
                                            padding: pxToVw(settings.buttons.mapButton.buttonPadding) || '10px',
                                            width: pxToVw(settings.buttons.mapButton.buttonWidth || '5vw'),
                                            border: `${pxToVw(settings.buttons.mapButton.borderWidth)} solid ${settings.buttons.mapButton.borderColor}`,
                                        }}
                                    >
                                        {settings.buttons.mapButton.text || "Map"} (Active)
                                    </button>
                                </Box>
                            )}

                        <div className="row" style={{ marginBottom: '15px' }}>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Button Gap (px)</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={settings.buttons.gap ?? "10"}
                                            onChange={(e) => handleChange('buttons.gap', e.target.value)}
                                            placeholder="10"
                                            min="0"
                                        />
                                        <small className="form-text text-muted">Gap between Map and Text buttons</small>
                                    </div>
                                </div>
                            </div>

                            <div className="row" style={{ marginBottom: '15px' }}>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Button Text</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={settings.buttons.mapButton.text || ""}
                                            onChange={(e) => handleChange("buttons.mapButton.text", e.target.value)}
                                            placeholder="Button text"
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Button Width (%)</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={settings.buttons.mapButton.buttonWidth ?? "100"}
                                            onChange={(e) => handleChange('buttons.mapButton.buttonWidth', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="row" style={{ marginBottom: '15px' }}>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Button Height (px)</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={settings.buttons.mapButton.buttonHeight ?? "40"}
                                            onChange={(e) => handleChange('buttons.mapButton.buttonHeight', e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Button Font Size (px)</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={settings.buttons.mapButton.buttonFontSize ?? "14"}
                                            onChange={(e) => handleChange('buttons.mapButton.buttonFontSize', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="row" style={{ marginBottom: '15px' }}>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Button Padding (px)</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={settings.buttons.mapButton.buttonPadding ?? "10"}
                                            onChange={(e) => handleChange('buttons.mapButton.buttonPadding', e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Border Width (px)</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={settings.buttons.mapButton.borderWidth ?? "1"}
                                            onChange={(e) => handleChange('buttons.mapButton.borderWidth', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="row" style={{ marginBottom: '15px' }}>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Border Radius (px)</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={settings.buttons.mapButton.borderRadius ?? "0"}
                                            onChange={(e) => handleChange('buttons.mapButton.borderRadius', e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Background Color</label>
                                        <MuiColorInput
                                            format="hex"
                                            value={settings.buttons.mapButton.backgroundColor || "#1976d2"}
                                            onChange={(value) => handleChange("buttons.mapButton.backgroundColor", value)}
                                            fullWidth
                                            size="small"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="row" style={{ marginBottom: '15px' }}>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Active Background Color</label>
                                        <MuiColorInput
                                            format="hex"
                                            value={settings.buttons.mapButton.activeBackgroundColor || "#115293"}
                                            onChange={(value) => handleChange("buttons.mapButton.activeBackgroundColor", value)}
                                            fullWidth
                                            size="small"
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Button Text Color</label>
                                        <MuiColorInput
                                            format="hex"
                                            value={settings.buttons.mapButton.buttonTextColor || "#ffffff"}
                                            onChange={(value) => handleChange("buttons.mapButton.buttonTextColor", value)}
                                            fullWidth
                                            size="small"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="row" style={{ marginBottom: '15px' }}>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Button Active Text Color</label>
                                        <MuiColorInput
                                            format="hex"
                                            value={settings.buttons.mapButton.buttonActiveTextColor || "#ffffff"}
                                            onChange={(value) => handleChange("buttons.mapButton.buttonActiveTextColor", value)}
                                            fullWidth
                                            size="small"
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Border Color</label>
                                        <MuiColorInput
                                            format="hex"
                                            value={settings.buttons.mapButton.borderColor || "#000000"}
                                            onChange={(value) => handleChange("buttons.mapButton.borderColor", value)}
                                            fullWidth
                                            size="small"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </TabPanel>

                    {/* Text Button Tab */}
                    <TabPanel value={activeTab} index={4}>
                        <div className="form-group">
                            {/* Text Button Preview */}
                            {renderPreview(
                                "Text Button Preview",
                                <Box display="flex" gap={3} flexWrap="wrap">
                                    <button
                                        style={{
                                            height: pxToVw(settings.buttons.textButton.buttonHeight || '10'),
                                            backgroundColor: settings.buttons.textButton.backgroundColor,
                                            borderRadius: pxToVw(settings.buttons.textButton.borderRadius) || '0px',
                                            fontSize: pxToVw(settings.buttons.textButton.buttonFontSize) || '14px',
                                            fontFamily: settings.fontFamily || 'Arial',
                                            color: settings.buttons.textButton.buttonTextColor || '#ffffff',
                                            padding: pxToVw(settings.buttons.textButton.buttonPadding) || '10px',
                                            width: pxToVw(settings.buttons.textButton.buttonWidth || '5vw'),
                                            border: `${pxToVw(settings.buttons.textButton.borderWidth)} solid ${settings.buttons.textButton.borderColor}`,
                                            marginRight: '20px',
                                        }}
                                    >
                                        {settings.buttons.textButton.text || "Text"}
                                    </button>
                                    <button
                                        style={{
                                            height: pxToVw(settings.buttons.textButton.buttonHeight || '10'),
                                            backgroundColor: settings.buttons.textButton.activeBackgroundColor,
                                            borderRadius: pxToVw(settings.buttons.textButton.borderRadius) || '0px',
                                            fontSize: pxToVw(settings.buttons.textButton.buttonFontSize) || '14px',
                                            fontFamily: settings.fontFamily || 'Arial',
                                            color: settings.buttons.textButton.buttonActiveTextColor || '#ffffff',
                                            padding: pxToVw(settings.buttons.textButton.buttonPadding) || '10px',
                                            width: pxToVw(settings.buttons.textButton.buttonWidth || '5vw'),
                                            border: `${pxToVw(settings.buttons.textButton.borderWidth)} solid ${settings.buttons.textButton.borderColor}`,
                                        }}
                                    >
                                        {settings.buttons.textButton.text || "Text"} (Active)
                                    </button>
                                </Box>
                            )}

                            <div className="row" style={{ marginBottom: '15px' }}>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Button Text</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={settings.buttons.textButton.text || ""}
                                            onChange={(e) => handleChange("buttons.textButton.text", e.target.value)}
                                            placeholder="Button text"
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Button Width (%)</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={settings.buttons.textButton.buttonWidth ?? "100"}
                                            onChange={(e) => handleChange('buttons.textButton.buttonWidth', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="row" style={{ marginBottom: '15px' }}>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Button Height (px)</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={settings.buttons.textButton.buttonHeight ?? "40"}
                                            onChange={(e) => handleChange('buttons.textButton.buttonHeight', e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Button Font Size (px)</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={settings.buttons.textButton.buttonFontSize ?? "14"}
                                            onChange={(e) => handleChange('buttons.textButton.buttonFontSize', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="row" style={{ marginBottom: '15px' }}>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Button Padding (px)</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={settings.buttons.textButton.buttonPadding ?? "10"}
                                            onChange={(e) => handleChange('buttons.textButton.buttonPadding', e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Border Width (px)</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={settings.buttons.textButton.borderWidth ?? "1"}
                                            onChange={(e) => handleChange('buttons.textButton.borderWidth', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="row" style={{ marginBottom: '15px' }}>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Border Radius (px)</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={settings.buttons.textButton.borderRadius ?? "0"}
                                            onChange={(e) => handleChange('buttons.textButton.borderRadius', e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Background Color</label>
                                        <MuiColorInput
                                            format="hex"
                                            value={settings.buttons.textButton.backgroundColor || "#1976d2"}
                                            onChange={(value) => handleChange("buttons.textButton.backgroundColor", value)}
                                            fullWidth
                                            size="small"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="row" style={{ marginBottom: '15px' }}>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Active Background Color</label>
                                        <MuiColorInput
                                            format="hex"
                                            value={settings.buttons.textButton.activeBackgroundColor || "#115293"}
                                            onChange={(value) => handleChange("buttons.textButton.activeBackgroundColor", value)}
                                            fullWidth
                                            size="small"
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Button Text Color</label>
                                        <MuiColorInput
                                            format="hex"
                                            value={settings.buttons.textButton.buttonTextColor || "#ffffff"}
                                            onChange={(value) => handleChange("buttons.textButton.buttonTextColor", value)}
                                            fullWidth
                                            size="small"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="row" style={{ marginBottom: '15px' }}>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Button Active Text Color</label>
                                        <MuiColorInput
                                            format="hex"
                                            value={settings.buttons.textButton.buttonActiveTextColor || "#ffffff"}
                                            onChange={(value) => handleChange("buttons.textButton.buttonActiveTextColor", value)}
                                            fullWidth
                                            size="small"
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label>Border Color</label>
                                        <MuiColorInput
                                            format="hex"
                                            value={settings.buttons.textButton.borderColor || "#000000"}
                                            onChange={(value) => handleChange("buttons.textButton.borderColor", value)}
                                            fullWidth
                                            size="small"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </TabPanel>

                    {/* Preview Tab */}
                    <TabPanel value={activeTab} index={5}>
                        <div className="form-group">
                            <Typography variant="h6" gutterBottom style={{ marginBottom: '30px' }}>
                                Full Screen Preview
                            </Typography>

                            {/* Header Preview */}

                            {/* Header Preview */}
                            <Box
                                sx={{
                                    backgroundColor: settings.header.backgroundColor,
                                    padding: pxToVw(settings.headerPadding || "4"),
                                    marginBottom: pxToVw(settings.headerMarginBottom || "10"),
                                    borderRadius: "8px",
                                    border: "1px solid #ddd",
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                {/* LEFT: Logo */}
                                <Box
                                    sx={{
                                        width: "160px", // must match right spacer
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "flex-start",
                                    }}
                                >
                                    {settings.headerLogo?.enabled && settings.headerLogo?.fileUrl && (
                                        <img
                                            src={settings.headerLogo.fileUrl}
                                            alt="Logo"
                                            style={{
                                                width: `${settings.headerLogo.width}%`,
                                                maxWidth: "140px",
                                                height: "auto",
                                            }}
                                        />
                                    )}
                                </Box>

                                {/* CENTER: Content */}
                                <Box
                                    sx={{
                                        flex: 1,
                                        textAlign: "center",
                                    }}
                                >
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            fontFamily: settings.fontFamily || "Arial",
                                            fontWeight: settings.header.fontWeight || 600,
                                            color: settings.header.titleColor || "#000000",
                                            fontSize: pxToVw(settings.header.titleFontSize || "18"),
                                            marginBottom: pxToVw(settings.header.marginBottom || "4"),
                                        }}
                                    >
                                        {settings.header.title || "Company Name"}
                                    </Typography>

                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            color: settings.header.locationTextColor || "#000000",
                                            fontSize: pxToVw(settings.header.locationTextFontSize || "14"),
                                        }}
                                    >
                                        Location Name
                                    </Typography>
                                </Box>

                                {/* RIGHT: Spacer (balances logo width) */}
                                <Box
                                    sx={{
                                        width: "160px",
                                    }}
                                />
                            </Box>



                            {/* Buttons Preview */}
                            <Box mt={4}>
                                <Typography variant="h6" gutterBottom>
                                    Buttons Preview
                                </Typography>
                                <Box display="flex" gap={3} flexWrap="wrap">
                                    {/* Map Button */}
                                    <button
                                        style={{
                                            height: pxToVw(settings.buttons.mapButton.buttonHeight) || '40px',
                                            backgroundColor: settings.buttons.mapButton.backgroundColor,
                                            borderRadius: pxToVw(settings.buttons.mapButton.borderRadius) || '0px',
                                            fontSize: pxToVw(settings.buttons.mapButton.buttonFontSize) || '14px',
                                            fontFamily: settings.fontFamily || 'Arial',
                                            color: settings.buttons.mapButton.buttonTextColor || '#ffffff',
                                            padding: pxToVw(settings.buttons.mapButton.buttonPadding) || '10px',
                                            width: pxToVw(settings.buttons.mapButton.buttonWidth || '5vw'),
                                            border: `${pxToVw(settings.buttons.mapButton.borderWidth)} solid ${settings.buttons.mapButton.borderColor}`,
                                            marginRight: '20px',
                                        }}
                                    >
                                        {settings.buttons.mapButton.text || "Map"}
                                    </button>

                                    {/* Text Button */}
                                    <button
                                        style={{
                                            height: pxToVw(settings.buttons.textButton.buttonHeight || '10'),
                                            backgroundColor: settings.buttons.textButton.backgroundColor,
                                            borderRadius: pxToVw(settings.buttons.textButton.borderRadius) || '0px',
                                            fontSize: pxToVw(settings.buttons.textButton.buttonFontSize) || '14px',
                                            fontFamily: settings.fontFamily || 'Arial',
                                            color: settings.buttons.textButton.buttonTextColor || '#ffffff',
                                            padding: pxToVw(settings.buttons.textButton.buttonPadding) || '10px',
                                            width: pxToVw(settings.buttons.textButton.buttonWidth || '5vw'),
                                            border: `${pxToVw(settings.buttons.textButton.borderWidth)} solid ${settings.buttons.textButton.borderColor}`,
                                        }}
                                    >
                                        {settings.buttons.textButton.text || "Text"}
                                    </button>
                                </Box>
                            </Box>

                            {/* Active State Preview */}
                            <Box mt={4}>
                                <Typography variant="h6" gutterBottom>
                                    Active State Preview
                                </Typography>
                                <Box display="flex" gap={3} flexWrap="wrap">
                                    <button
                                        style={{
                                            height: pxToVw(settings.buttons.mapButton.buttonHeight) || '40px',
                                            backgroundColor: settings.buttons.mapButton.activeBackgroundColor,
                                            borderRadius: pxToVw(settings.buttons.mapButton.borderRadius) || '0px',
                                            fontSize: pxToVw(settings.buttons.mapButton.buttonFontSize) || '14px',
                                            fontFamily: settings.fontFamily || 'Arial',
                                            color: settings.buttons.mapButton.buttonActiveTextColor || '#ffffff',
                                            padding: pxToVw(settings.buttons.mapButton.buttonPadding) || '10px',
                                            width: pxToVw(settings.buttons.mapButton.buttonWidth || '5vw'),
                                            border: `${pxToVw(settings.buttons.mapButton.borderWidth)} solid ${settings.buttons.mapButton.borderColor}`,
                                            marginRight: '20px',
                                        }}
                                    >
                                        {settings.buttons.mapButton.text || "Map"} (Active)
                                    </button>

                                    <button
                                        style={{
                                            height: pxToVw(settings.buttons.textButton.buttonHeight || '10'),
                                            backgroundColor: settings.buttons.textButton.activeBackgroundColor,
                                            borderRadius: pxToVw(settings.buttons.textButton.borderRadius) || '0px',
                                            fontSize: pxToVw(settings.buttons.textButton.buttonFontSize) || '14px',
                                            fontFamily: settings.fontFamily || 'Arial',
                                            color: settings.buttons.textButton.buttonActiveTextColor || '#ffffff',
                                            padding: pxToVw(settings.buttons.textButton.buttonPadding) || '10px',
                                            width: pxToVw(settings.buttons.textButton.buttonWidth || '5vw'),
                                            border: `${pxToVw(settings.buttons.textButton.borderWidth)} solid ${settings.buttons.textButton.borderColor}`,
                                        }}
                                    >
                                        {settings.buttons.textButton.text || "Text"} (Active)
                                    </button>
                                </Box>
                            </Box>
                        </div>
                    </TabPanel>

                    <div className="page-header" style={{ marginBottom: '20px' }}>
                        <div className="row">
                            {/* <div className="col-md-6 col-sm-12">
                                <div className="title">
                                    <Typography variant="h4">
                                        Mobile Screen Settings
                                    </Typography>
                                </div>
                            </div> */}
                            <div className="col-md-12 col-sm-12 text-end" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                <button
                                    className="btn btn-primary"
                                    onClick={handleSubmit}
                                    disabled={saveLoading}
                                    style={{
                                        backgroundColor: buttonColor || "#1976d2",
                                        borderColor: buttonColor || "#1976d2",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                    }}
                                >
                                    {saveLoading ? "Saving..." : "Save Settings"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MobileScreenSettings;